import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { MdDoNotDisturb } from "react-icons/md";
import FloatingInput from "../common/FloatingInput";
import { censusBillingRules, dailyService } from "../services/soar";
import TextArea from "../common/TextArea";

function DailyService({ service, view, clientInfo, setData, bedAssignment }) {
  const [serviceData, setServiceData] = useState(service);
  const [refresh, setRefresh] = useState(false);
  const [rule, setRule] = useState([]);
  const [showRule, setShowRule] = useState(false);
  const navigate = useNavigate();

  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";

  function convertDateFormat(dateString) {
    const parts = dateString.split("/"); // Splits "MM/DD/YYYY" into ["MM", "DD", "YYYY"]
    const formattedDate = [parts[2], parts[0], parts[1]].join("-"); // Rearranges and joins with hyphens
    return formattedDate;
  }

  const p = clientInfo?.payors?.find(
    (payor) =>
      new Date(serviceData.serviceDate) >= new Date(payor.effective) &&
      payor.active &&
      (!payor.expiration ||
        new Date(serviceData.serviceDate) <= new Date(payor.expiration))
  );
  const m = clientInfo?.modalities?.find(
    (modality) =>
      new Date(serviceData.serviceDate) >=
        new Date(convertDateFormat(modality.ccx_startdate.date)) &&
      modality.status === "Active" &&
      (!modality.ccx_enddate ||
        new Date(serviceData.serviceDate) <=
          new Date(convertDateFormat(modality.ccx_enddate.date)))
  );

  useEffect(() => {
    if (clientInfo) payorModalityCheck();
  }, [clientInfo]);

  useEffect(() => {
    getRule();
  }, [
    serviceData.payor,
    serviceData.groupPlan,
    serviceData.program,
    serviceData.serviceConfiguration,
    serviceData.clientModality,
    serviceData.confirmed,
  ]);

  useEffect(() => {
    if (refresh) {
      getDailyService();
      setRefresh(false);
    }
  }, [refresh]);

  const getDailyService = async () => {
    const response = await dailyService({
      command: "query",
      application: applicationId,
      keyword: "byid",
      ids: [serviceData.id],
    });
    if (response.code === "success") {
      setServiceData(response.data);
    } else {
      toast.error("Error Fetching Daily Service");
    }
  };

  const updateService = async (data) => {
    const updatedService = {
      ...(data ? data : serviceData),
      command: "update",
      application: applicationId,
      id: serviceData.id,
      note: data ? data.note : serviceData.note,
      serviceStatus: data ? data.serviceStatus : serviceData.serviceStatus,
      residenceTier: residenceTierCheck(),
    };
    const response = await dailyService(updatedService);
    if (response.code === "success") {
      toast.success("Service Updated Successfully");
      setData((prevData) =>
        prevData.map((item) =>
          item.id === bedAssignment.id
            ? {
                ...item,
                services: item.services.map((s) =>
                  s.id === service.id ? updatedService : s
                ),
              }
            : item
        )
      );
    } else {
      toast.error("Error Updating Service");
    }
  };

  const residenceTierCheck = () => {
    const tier = serviceData?.residenceRelated?.find(
      (r) =>
        r.type === "FP Tier" &&
        r.active &&
        new Date(r.effective) <= new Date(serviceData.serviceDate) &&
        (!r.expiration ||
          new Date(serviceData.serviceDate) <= new Date(r.expiration))
    );
    return tier ? tier.value : "1";
  };

  const payorModalityCheck = async () => {
    setServiceData({
      ...serviceData,
      payor: p?.payorId,
      payorName: p?.payorName,
      groupPlan: p?.groupPlanId,
      groupPlanName: p?.groupPlanName,
      clientModality: m?.ccx_caremodalityid,
      clientModalityName: m?.ccx_caremodality.name,
      clientMPI: m?.contact?.mpi,
    });
  };

  const confirmBedDay = async () => {
    const response = await dailyService({
      command: "confirm",
      application: applicationId,
      id: serviceData.id,
      note: serviceData.note,
      serviceStatus: serviceData.serviceStatus,
    });
    if (response.code === "success") {
      toast.success("Bed Day Confirmed");
      // const obj = {
      //   ...serviceData,
      //   confirmed: true,
      //   residenceTier: residenceTierCheck(),
      //   payor: p?.payorId,
      //   payorName: p?.payorName,
      //   groupPlan: p?.groupPlanId,
      //   groupPlanName: p?.groupPlanName,
      //   clientModality: m?.ccx_caremodalityid,
      //   clientModalityName: m?.ccx_caremodality.name,
      //   billingRule: matchingRule()?.id,
      //   billingRuleName: `${matchingRule()?.modalityConfig.name} - ${
      //     matchingRule()?.details[serviceData.serviceStatus]
      //   } - Tier ${serviceData.residenceTier}`,
      //   billingRuleSubset: matchingRule()?.details,
      //   billingRuleRevenue: matchingRule()?.details[serviceData.serviceStatus],
      //   billingRuleReimbursement:
      //     matchingRule()?.details[
      //       `${serviceData.serviceStatus}-tier${serviceData.residenceTier}`
      //     ],
      //   glCode: matchingRule()?.modalityConfig.glCode,
      //   billingRuleNotes: [],
      // };

      // const updateServiceResponse = await dailyService({
      //   ...obj,
      //   command: "billingupdate",
      // });
      // if (updateServiceResponse.code === "success") {
      //   toast.success("Updated Service Successfully");
      //   setServiceData(obj);
      // } else {
      //   toast.error("Error Updating Service");
      // }
    } else {
      toast.error("Error Confirming Bed Day");
    }
  };

  const unConfirmBedDay = async () => {
    const response = await dailyService({
      command: "unconfirm",
      application: applicationId,
      id: serviceData.id,
    });
    if (response.code === "success") {
      toast.success("Bed Day Unconfirmed");
      setServiceData({
        ...serviceData,
        confirmed: false,
        billingRule: null,
        billingRuleName: null,
        billingRuleSubset: null,
        billingRuleRevenue: null,
        billingRuleReimbursement: null,
        glCode: null,
        billingRuleNotes: [],
      });
    } else {
      toast.error("Error Unconfirming Bed Day");
    }
  };

  const getRule = async () => {
    const response = await censusBillingRules({
      command: "query",
      application: applicationId,
      keyword: "allinterm",
      searchTerms: {
        payor: serviceData.payor,
        groupPlan: serviceData.groupPlan,
        serviceConfiguration: serviceData.serviceConfiguration,
        program: serviceData.program,
      },
    });
    if (response.code === "success") {
      setRule(response.data);
    } else {
      toast.error("Error Finding Rule");
    }
  };

  const matchingRule = () => {
    const activeRule = rule.find((r) => r.published && !r.expiration);

    const matchingModality = activeRule?.configuration.modalities.find(
      (m) => m.name === serviceData.clientModalityName
    );

    const age = matchingModality?.ages.find(
      (a) =>
        (a.min <= serviceData.age && serviceData.age <= a.max) ||
        (a.min === "" && a.max === "")
    );

    return {
      id: activeRule?.id,
      modalityConfig: matchingModality,
      details: age,
    };
  };

  return (
    <div>
      <div className="my-4">
        <div className="card card-safy">
          <div className="card-header bg-safy1-1 text-white fw-light">
            <div className="row mb-1">
              <div className="col d-flex">
                <div
                  className="btn btn-info"
                  onClick={() => navigate(`/client/${serviceData.client}`)}
                >
                  <i className="fas fa-user me-2" />
                  {serviceData.clientName}
                </div>
                {!serviceData.payor && (
                  <div className="rounded bg-danger text-white p-2 fw-bold ms-2">
                    <MdDoNotDisturb size="1.5em" className="fw-bold" />P
                  </div>
                )}
                {!serviceData.clientModality && (
                  <div className="rounded bg-danger text-white p-2 fw-bold ms-2">
                    <MdDoNotDisturb size="1.5em" className="fw-bold" />M
                  </div>
                )}
              </div>
            </div>
            <div className="row h6 fw-light">
              <div className="col">{`MPI: ${clientInfo?.mpi}`}</div>
              <div className="col">
                {`DOB: ${DateTime.fromISO(clientInfo?.dob).toLocaleString(
                  DateTime.DATE_SHORT
                )}`}
              </div>
              <div className="col">Census Date</div>
              <div className="col">
                <div className="fw-bold">
                  {DateTime.fromISO(serviceData.serviceDate).toLocaleString(
                    DateTime.DATE_SHORT
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div
              className={
                serviceData.confirmed ? "border border-dark bg-light p-1" : ""
              }
            >
              <div className="row">
                <div className="col">Foster Home</div>
                <div className="col fw-bold">
                  <div
                    className="btn btn-sm btn-info"
                    onClick={() =>
                      navigate(
                        `/residence/fosterhomes/${serviceData.residence}`
                      )
                    }
                  >
                    <i className="fas fa-home me-2" />
                    {serviceData.residenceName}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">Bed Name</div>
                <div className="col fw-bold">{serviceData.bedName}</div>
              </div>
              <div className="row mb-2">
                <div className="col">Service Configuration</div>
                <div className="col fw-bold">
                  {serviceData.serviceConfigurationName}
                </div>
              </div>
              <div className="row">
                <div className="col-9">
                  <FloatingInput
                    type="select"
                    name="serviceStatus"
                    label="Service Status"
                    options={serviceData.serviceStatuses?.map((ss) => ({
                      _id: ss,
                      name: ss,
                    }))}
                    value={serviceData.serviceStatus}
                    onChange={(e) => {
                      setServiceData({
                        ...serviceData,
                        serviceStatus: e.target.value,
                      });
                      updateService({
                        ...serviceData,
                        serviceStatus: e.target.value,
                      });
                    }}
                    disabled={serviceData.confirmed}
                  />
                </div>
                <div className="col-3">
                  {(!serviceData.confirmed ||
                    serviceData.confirmed === false) && (
                    <div
                      className="btn btn-success float-end mt-4"
                      onClick={() => {
                        confirmBedDay();
                      }}
                    >
                      Confirm
                    </div>
                  )}
                </div>
              </div>

              <FloatingInput
                name="notes"
                label="Notes"
                value={serviceData.note}
                onChange={(e) =>
                  setServiceData({
                    ...serviceData,
                    note: e.currentTarget.value,
                  })
                }
              />
              <div className="row m-0 p-0">
                <div className="col-3 ps-2">
                  <div
                    className="btn btn-primary"
                    onClick={() => updateService()}
                  >
                    Add Note
                  </div>
                </div>
              </div>

              <div className="row mt-4">
                {serviceData.confirmed === true && (
                  <>
                    <div className="col">Date Confirmed</div>
                    <div className="col fw-bold">
                      {DateTime.fromISO(
                        serviceData.confirmedDate
                      ).toLocaleString(DateTime.DATE_SHORT)}
                    </div>
                    <div className="col">Confirmed By</div>
                    <div className="col fw-bold">
                      {serviceData.confirmedByName}
                      <i
                        className="fas fa-trash text-danger ms-2"
                        onClick={() => unConfirmBedDay()}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            {view !== "Bed" && (
              <>
                <div className="row mt-4">
                  <div className="col">Client Modality</div>
                  <div className="col fw-bold">
                    {serviceData.clientModalityName}
                  </div>
                </div>
                <div className="row">
                  <div className="col">Payor</div>
                  <div className="col fw-bold">{serviceData.payorName}</div>
                </div>
                <div className="row">
                  <div className="col">Group Plan</div>
                  <div className="col fw-bold">{serviceData.groupPlanName}</div>
                </div>
                <div className="row">
                  <div className="col">FP Tier</div>
                  <div className="col fw-bold">{residenceTierCheck()}</div>
                </div>
                <div className="mt-4">
                  <div className="row">
                    <div className="col">Billing Rule ID:</div>
                    <div className="col">
                      {(
                        <div
                          className="btn btn-sm btn-info"
                          onClick={() =>
                            navigate(`/rev/rules/${matchingRule()?.id}`)
                          }
                        >
                          {matchingRule()?.id}
                        </div>
                      ) || ""}
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col">Billing</div>
                    <div className="col fw-bold">
                      {`$${
                        (serviceData.serviceStatus &&
                          matchingRule()?.details &&
                          matchingRule()?.details[serviceData.serviceStatus]) ||
                        ""
                      }`}
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col">Maintenance</div>
                    <div className="col fw-bold">
                      {`$${
                        serviceData.serviceStatus && matchingRule()?.details
                          ? matchingRule()?.details[
                              `${serviceData.serviceStatus}-tier${serviceData.residenceTier}`
                            ]
                          : ""
                      }`}
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col">Admin</div>
                    <div className="col fw-bold">
                      {`$${
                        (serviceData.serviceStatus &&
                          matchingRule()?.details &&
                          matchingRule()?.details[serviceData.serviceStatus]) -
                        matchingRule()?.details?.[
                          `${serviceData.serviceStatus}-tier${serviceData.residenceTier}`
                        ]
                      }`}
                    </div>
                  </div>
                  {view === "Invoice/Payments" && (
                    <>
                      <br />
                      <div className="row mt-4">
                        <div className="col">Invoice ID</div>
                        <div className="col fw-bold">
                          {serviceData.invoiceId}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col">Invoice Date</div>
                        <div className="col fw-bold">
                          {serviceData.invoiceDate}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col">Payment ID</div>
                        <div className="col fw-bold">
                          {serviceData.paymentId}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col">Payment Date</div>
                        <div className="col fw-bold">
                          {serviceData.paymentDate}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col">Reimbursement ID</div>
                        <div className="col fw-bold">
                          {serviceData.reimbursementId}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col">Reimbursement Date</div>
                        <div className="col fw-bold">
                          {serviceData.reimbursementDate}
                        </div>
                      </div>
                      <div className="my-4"></div>
                      <div className="h5">Adjustments</div>
                      <div className="rounded border border-dark p-2 bg-light">
                        <div className="row fw-bold">
                          <div className="col">Type</div>
                          <div className="col">Date</div>
                          <div className="col">Adj Out</div>
                          <div className="col">Adj In</div>
                          <div className="col"></div>
                        </div>
                        <div className="row">
                          <div className="col">Service Status</div>
                          <div className="col">3/12/2025</div>
                          <div className="col text-danger">-$112.04</div>
                          <div className="col text-success">$.0.00</div>
                          <div className="col">
                            <i className="fas fa-eye text-primary" />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">FP Reimburse</div>
                          <div className="col">3/12/2025</div>
                          <div className="col text-danger">-$32.90</div>
                          <div className="col text-success">$.0.00</div>
                          <div className="col">
                            <i className="fas fa-eye text-primary" />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="mt-4">
          <i
            className="fas fa-info text-primary"
            onClick={() => setShowRule(!showRule)}
          />
          {showRule && (
            <>
              <div className="rounded border border-dark bg-info p-2">
                <div className="fw-bold">{`Matching Rule ID: ${
                  matchingRule()?.id
                }`}</div>
                <div className="mt-2">
                  <TextArea
                    name="ruleJSON"
                    label="Rule Info"
                    value={JSON.stringify(
                      matchingRule()?.details,
                      undefined,
                      3
                    )}
                  />
                </div>
                {serviceData.serviceStatus !== "" && (
                  <div>
                    <div className="h5">Rule Status</div>
                    <div className="row p-2">
                      <div className="col rounded bg-success text-white p-2 m-1">{`${
                        serviceData.serviceStatus
                      } billing $${
                        matchingRule()?.details?.[serviceData.serviceStatus]
                      }`}</div>
                      <div className="col rounded bg-success text-white p-2 m-1">{`${
                        serviceData.serviceStatus
                      } Reimbursement $${
                        matchingRule()?.details?.[
                          `${serviceData.serviceStatus}-tier${serviceData.fosterParentTier}`
                        ]
                      }`}</div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DailyService;
